// src/components/voice/CosmicField.tsx
//
// Fondo cósmico para el módulo Voz: partículas en órbita alrededor de un
// agujero negro, proyectadas en 3D con Canvas2D puro (sin three.js). Se
// pintan desde z-atrás-hacia-adelante para que el glow del frente tape
// partículas lejanas.
//
// Decisiones:
//  · Canvas 2D en lugar de WebGL — bundle ~0 bytes extra (ya incluído en
//    el navegador). Perf en móvil gama media: ~250 partículas @ 60 fps.
//  · Cantidad adaptativa según `navigator.deviceMemory` + prefers-
//    reduced-motion. Si el usuario pidió reducir movimiento pintamos un
//    starfield estático (un solo frame) y ya.
//  · El estado del WebSocket (listening / speaking / thinking / user_
//    speaking / …) controla la paleta, la agitación y el color del
//    horizonte. Todos los tweens ocurren dentro del rAF → cero
//    re-renders de React.
//  · Pausa automática cuando la pestaña pierde foco (visibilitychange).
//  · Tilt del giroscopio entra como rotación x/y del espacio antes de
//    proyectar.

import { useEffect, useRef } from 'react';

import type { VoiceStatus } from '@/hooks/useVoiceWebSocket';

export type IntensityLevel = 'calm' | 'listen' | 'excited' | 'dim';

interface Props {
  /** Estado WebSocket activo — define la paleta. */
  status: VoiceStatus;
  /** Tilt normalizado [-1, 1]. El padre lo setea por giro o mouse. */
  tilt: { x: number; y: number };
  /** Amplitud de voz aproximada [0, 1] — sirve para pulsar el horizonte.
   *  Si el hook no reporta esto, déjalo en 0. */
  audioLevel?: number;
  /** Render más discreto cuando el canvas está detrás de una modal. */
  dim?: boolean;
}

// ═══════════════════════════════════════════════════════════════
// Paleta por estado
// ═══════════════════════════════════════════════════════════════

interface Palette {
  hue: number;          // core hue, 0–360
  hueSpread: number;    // +/- variación por partícula
  accent: string;       // color del horizonte
  glow: number;         // 0..1 intensidad del bloom
  agitation: number;    // 0..1 ruido extra en velocidades
}

const PALETTES: Record<VoiceStatus, Palette> = {
  disconnected:  { hue: 220, hueSpread: 40, accent: '#3b82f6', glow: 0.25, agitation: 0.05 },
  connecting:    { hue: 48,  hueSpread: 30, accent: '#eab308', glow: 0.4,  agitation: 0.15 },
  listening:     { hue: 160, hueSpread: 40, accent: '#10b981', glow: 0.5,  agitation: 0.08 },
  user_speaking: { hue: 200, hueSpread: 60, accent: '#38bdf8', glow: 0.8,  agitation: 0.45 },
  thinking:      { hue: 280, hueSpread: 50, accent: '#a855f7', glow: 0.6,  agitation: 0.25 },
  speaking:      { hue: 36,  hueSpread: 60, accent: '#f59e0b', glow: 0.9,  agitation: 0.55 },
  error:         { hue: 0,   hueSpread: 30, accent: '#ef4444', glow: 0.3,  agitation: 0.1 },
};

// ═══════════════════════════════════════════════════════════════
// Partículas
// ═══════════════════════════════════════════════════════════════

interface Particle {
  // posición en espacio 3D; el centro del agujero negro está en (0,0,0)
  x: number; y: number; z: number;
  // velocidad
  vx: number; vy: number; vz: number;
  // propiedades visuales
  radius: number;
  hueOffset: number;   // se suma al hue del palette
  trail: number;       // 0..1 — probabilidad de dejar estela
}

function makeParticle(dim = 1): Particle {
  // Distribución en una cáscara esférica — evita que empiecen todas en el
  // centro y se auto-destruyan el primer frame.
  const r = 600 + Math.random() * 600;
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  const x = r * Math.sin(phi) * Math.cos(theta);
  const y = r * Math.sin(phi) * Math.sin(theta);
  const z = r * Math.cos(phi);
  // Velocidad tangencial — da el empuje orbital inicial; sin esto las
  // partículas caerían radialmente y la escena se siente muerta.
  const speed = 0.35 + Math.random() * 0.5;
  const tangent = cross({ x, y, z }, { x: 0, y: 1, z: 0 });
  const tn = norm(tangent);
  return {
    x, y, z,
    vx: (tangent.x / tn) * speed,
    vy: (tangent.y / tn) * speed,
    vz: (tangent.z / tn) * speed,
    radius: (0.6 + Math.random() * 1.6) * dim,
    hueOffset: (Math.random() - 0.5) * 2,
    trail: Math.random() < 0.15 ? 1 : 0,
  };
}

function cross(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}
function norm(v: { x: number; y: number; z: number }) {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
}

// ═══════════════════════════════════════════════════════════════
// Componente
// ═══════════════════════════════════════════════════════════════

export function CosmicField({ status, tilt, audioLevel = 0, dim = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    // ── adaptive quality ─────────────────────────────────────
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const deviceMemory = (navigator as any).deviceMemory ?? 4;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    // base count tuned para móvil gama media. 64 en modo reducido, 500
    // desktop con >= 4 GB.
    let particleCount = reducedMotion
      ? 64
      : isMobile
      ? deviceMemory >= 4 ? 260 : 160
      : deviceMemory >= 4 ? 480 : 320;

    // ── resize + DPR ────────────────────────────────────────
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let viewW = 0, viewH = 0;
    function resize() {
      const rect = canvas.getBoundingClientRect();
      viewW = rect.width;
      viewH = rect.height;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    // ── particle population ─────────────────────────────────
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) particles.push(makeParticle());

    // ── animated palette (tween para evitar cortes de color) ──
    const current: Palette = { ...PALETTES[status] };
    // Expose a mutable refs dict so the tween can read latest values set by
    // props without re-creating the rAF loop.
    const live = {
      palette: PALETTES[status],
      tilt: { x: tilt.x, y: tilt.y },
      audioLevel,
      dim,
    };

    // ── tiny starfield for reduced-motion (one paint) ────────
    if (reducedMotion) {
      ctx.fillStyle = '#05050f';
      ctx.fillRect(0, 0, viewW, viewH);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      for (let i = 0; i < 300; i++) {
        ctx.fillRect(Math.random() * viewW, Math.random() * viewH, 1, 1);
      }
      const center = { x: viewW / 2, y: viewH / 2 };
      const grad = ctx.createRadialGradient(center.x, center.y, 10, center.x, center.y, Math.max(viewW, viewH) / 2);
      grad.addColorStop(0, 'rgba(0,0,0,0.95)');
      grad.addColorStop(0.2, 'rgba(0,0,0,0.75)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, viewW, viewH);
      return () => {
        resizeObserver.disconnect();
      };
    }

    // ── main rAF loop ───────────────────────────────────────
    let paused = false;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    let last = performance.now();
    let rafId = 0;
    // auto-quality: si los frames se alargan, rebaja partículas.
    let slowFrames = 0;

    // constants
    const FOCAL = 480;
    const EVENT_HORIZON = 80;
    const G = 2800;
    const ORBITAL = 260;

    const render = (now: number) => {
      if (paused) {
        last = now;
        rafId = requestAnimationFrame(render);
        return;
      }
      const dt = Math.min(1 / 20, (now - last) / 1000);
      last = now;

      // auto-quality probe
      if (dt > 0.04) slowFrames++; else slowFrames = Math.max(0, slowFrames - 1);
      if (slowFrames > 30 && particles.length > 64) {
        particles.length = Math.floor(particles.length * 0.75);
        slowFrames = 0;
      }

      // palette tween (0.06 per frame ≈ ~15 frames to snap)
      const target = live.palette;
      current.hue += (target.hue - current.hue) * 0.06;
      current.hueSpread += (target.hueSpread - current.hueSpread) * 0.06;
      current.glow += (target.glow - current.glow) * 0.06;
      current.agitation += (target.agitation - current.agitation) * 0.08;

      // background — motion trail con alpha residual
      ctx.fillStyle = `rgba(3, 3, 12, ${live.dim ? 0.45 : 0.22})`;
      ctx.fillRect(0, 0, viewW, viewH);

      const cx = viewW / 2;
      const cy = viewH / 2;
      const tiltXrad = live.tilt.x * 0.45;
      const tiltYrad = live.tilt.y * 0.45;
      const cosY = Math.cos(tiltXrad);
      const sinY = Math.sin(tiltXrad);
      const cosX = Math.cos(tiltYrad);
      const sinX = Math.sin(tiltYrad);

      // event horizon pulse
      const horizonPulse = 1 + Math.sin(now / 600) * 0.04 + live.audioLevel * 0.35;

      // actualizar + proyectar. Hacemos bucket-sort por z para dibujar
      // de atrás hacia adelante sin ordenar el array (costo ~O(n)).
      const BUCKETS = 16;
      const buckets: Particle[][] = Array.from({ length: BUCKETS }, () => []);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        // gravedad hacia el origen (agujero negro)
        const r2 = p.x * p.x + p.y * p.y + p.z * p.z;
        const r = Math.sqrt(r2);
        if (r < EVENT_HORIZON || r > 2400) {
          // reinyectar en el borde externo
          const np = makeParticle();
          p.x = np.x; p.y = np.y; p.z = np.z;
          p.vx = np.vx; p.vy = np.vy; p.vz = np.vz;
          p.radius = np.radius; p.hueOffset = np.hueOffset; p.trail = np.trail;
          continue;
        }
        const gAccel = G / r2;
        const ax = -p.x / r * gAccel;
        const ay = -p.y / r * gAccel;
        const az = -p.z / r * gAccel;
        // empuje tangencial (mantiene espiral en vez de caída radial)
        const tangent = cross({ x: p.x, y: p.y, z: p.z }, { x: 0, y: 1, z: 0 });
        const tn = norm(tangent);
        const ob = ORBITAL / r;
        p.vx += (ax + (tangent.x / tn) * ob) * dt;
        p.vy += (ay + (tangent.y / tn) * ob) * dt;
        p.vz += (az + (tangent.z / tn) * ob) * dt;
        // agitación por estado
        if (current.agitation > 0) {
          const n = current.agitation * 4;
          p.vx += (Math.random() - 0.5) * n * dt;
          p.vy += (Math.random() - 0.5) * n * dt;
          p.vz += (Math.random() - 0.5) * n * dt;
        }
        // dampening
        p.vx *= 0.995;
        p.vy *= 0.995;
        p.vz *= 0.995;
        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;

        // aplicar tilt del giroscopio: rotamos Y luego X
        let rx = p.x * cosY + p.z * sinY;
        let rz = -p.x * sinY + p.z * cosY;
        const ry = p.y * cosX - rz * sinX;
        rz = p.y * sinX + rz * cosX;

        // cámara al frente — empuja la escena hacia +z para que no
        // aparezca detrás del plano de proyección
        const zc = rz + 900;
        if (zc <= 50) continue;
        const scale = FOCAL / zc;
        const sx = cx + rx * scale;
        const sy = cy + ry * scale;
        if (sx < -100 || sx > viewW + 100 || sy < -100 || sy > viewH + 100) continue;

        const bucket = Math.min(BUCKETS - 1, Math.floor((rz + 1200) / 2400 * BUCKETS));
        // reutilizamos los campos del particle para pasar datos al bucket
        (p as any)._sx = sx;
        (p as any)._sy = sy;
        (p as any)._sc = scale;
        buckets[bucket].push(p);
      }

      // draw farther → nearer
      const hueBase = current.hue;
      const hueSpread = current.hueSpread;
      const glow = current.glow;
      for (let b = BUCKETS - 1; b >= 0; b--) {
        const row = buckets[b];
        for (let i = 0; i < row.length; i++) {
          const p = row[i];
          const sx = (p as any)._sx;
          const sy = (p as any)._sy;
          const sc = (p as any)._sc;
          const h = hueBase + p.hueOffset * hueSpread;
          const alpha = Math.min(1, 0.2 + sc * 0.9);
          ctx.globalAlpha = alpha;
          ctx.fillStyle = `hsl(${h}, 85%, 70%)`;
          ctx.beginPath();
          ctx.arc(sx, sy, Math.max(0.4, p.radius * sc), 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      // ── event horizon (orbe central) ──────────────────────
      const halo = ctx.createRadialGradient(
        cx, cy, EVENT_HORIZON * 0.3 * horizonPulse,
        cx, cy, EVENT_HORIZON * 5 * horizonPulse,
      );
      halo.addColorStop(0, `${hexToRgba(current.accent, 0.9 * glow)}`);
      halo.addColorStop(0.2, `${hexToRgba(current.accent, 0.35 * glow)}`);
      halo.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, viewW, viewH);

      // núcleo oscuro (el horizonte propiamente dicho)
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, EVENT_HORIZON * horizonPulse);
      core.addColorStop(0, '#000');
      core.addColorStop(0.85, '#000');
      core.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy, EVENT_HORIZON * horizonPulse * 1.2, 0, Math.PI * 2);
      ctx.fill();

      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    // sync prop → ref cuando el padre cambia
    const sync = () => {
      live.palette = PALETTES[statusRef.current];
      live.tilt = { x: tiltRef.current.x, y: tiltRef.current.y };
      live.audioLevel = audioRef.current;
      live.dim = dimRef.current;
    };
    const syncIv = setInterval(sync, 80);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(syncIv);
      document.removeEventListener('visibilitychange', onVis);
      resizeObserver.disconnect();
    };
    // Intencionalmente dejamos el array de dependencias vacío — la escena
    // se inicializa una sola vez y los valores dinámicos (status, tilt)
    // fluyen vía refs para no resetear el rAF.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Props → refs (evitan re-crear rAF cada render)
  const statusRef = useRef(status);
  const tiltRef = useRef(tilt);
  const audioRef = useRef(audioLevel);
  const dimRef = useRef(dim);
  statusRef.current = status;
  tiltRef.current = tilt;
  audioRef.current = audioLevel;
  dimRef.current = dim;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ background: 'radial-gradient(ellipse at center, #0a0a20 0%, #02020a 60%, #000 100%)' }}
    />
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return `rgba(255,255,255,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export default CosmicField;
